import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Mail, Server, Lock, Shield, Eye, EyeOff, Send, RefreshCw, Check, X, AlertTriangle, Info, Save, TestTube, Plug, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { tokenStore } from "@/lib/api";
import { HelpIcon } from "@/components/HelpIcon";
import { toast } from "sonner";

const SMTP_PRESETS = [
  { id: "custom", name: "Custom SMTP Server", host: "", port: "" },
  { id: "sendgrid", name: "SendGrid", host: "smtp.sendgrid.net", port: "587" },
  { id: "mailgun", name: "Mailgun", host: "smtp.mailgun.org", port: "587" },
  { id: "ses", name: "Amazon SES", host: "email-smtp.us-east-1.amazonaws.com", port: "587" },
  { id: "gmail", name: "Gmail", host: "smtp.gmail.com", port: "587" },
  { id: "outlook", name: "Microsoft 365/Outlook", host: "smtp.office365.com", port: "587" },
  { id: "postmark", name: "Postmark", host: "smtp.postmarkapp.com", port: "587" },
];

const EMAIL_TEMPLATES = [
  { id: "welcome", name: "Welcome Email", subject: "Welcome to QCoreSecure", status: "active", lastModified: "2025-12-01" },
  { id: "password_reset", name: "Password Reset", subject: "Reset Your Password", status: "active", lastModified: "2025-12-01" },
  { id: "mfa_code", name: "MFA Verification Code", subject: "Your Verification Code", status: "active", lastModified: "2025-12-01" },
  { id: "api_key_created", name: "API Key Created", subject: "New API Key Generated", status: "active", lastModified: "2025-11-28" },
  { id: "usage_alert", name: "Usage Alert", subject: "API Usage Alert", status: "active", lastModified: "2025-11-25" },
  { id: "invoice", name: "Invoice Notification", subject: "Your Invoice is Ready", status: "active", lastModified: "2025-11-20" },
  { id: "subscription_renewal", name: "Subscription Renewal", subject: "Subscription Renewal Notice", status: "inactive", lastModified: "2025-11-15" },
];

export default function EmailConfiguration() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("smtp");
  const [showPassword, setShowPassword] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testStatus, setTestStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [testMessage, setTestMessage] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // SMTP Configuration State
  const [smtpPreset, setSmtpPreset] = useState("custom");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpEncryption, setSmtpEncryption] = useState("tls");
  const [smtpFromEmail, setSmtpFromEmail] = useState("");
  const [smtpFromName, setSmtpFromName] = useState("QCoreSecure");
  const [smtpReplyTo, setSmtpReplyTo] = useState("");
  const [smtpEnabled, setSmtpEnabled] = useState(true);

  // Rate Limiting
  const [rateLimitEnabled, setRateLimitEnabled] = useState(true);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState("60");
  const [rateLimitPerHour, setRateLimitPerHour] = useState("1000");

  const handlePresetChange = (presetId) => {
    setSmtpPreset(presetId);
    const preset = SMTP_PRESETS.find(p => p.id === presetId);
    if (preset && presetId !== "custom") {
      setSmtpHost(preset.host);
      setSmtpPort(preset.port);
    }
    setHasChanges(true);
  };

  // Load existing config from backend
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/settings/email`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
        });
        if (res.ok) {
          const d = await res.json();
          if (d.smtp_host) setSmtpHost(d.smtp_host);
          if (d.smtp_port) setSmtpPort(String(d.smtp_port));
          if (d.smtp_username) setSmtpUsername(d.smtp_username);
          if (d.has_password) setSmtpPassword(d.smtp_password_masked);
          if (d.smtp_encryption) setSmtpEncryption(d.smtp_encryption);
          if (d.from_email) setSmtpFromEmail(d.from_email);
          if (d.from_name) setSmtpFromName(d.from_name);
          if (d.reply_to) setSmtpReplyTo(d.reply_to);
          setSmtpEnabled(d.enabled ?? true);
          setRateLimitEnabled(d.rate_limit_enabled !== false);
          if (d.rate_limit_per_minute) setRateLimitPerMinute(String(d.rate_limit_per_minute));
          if (d.rate_limit_per_hour) setRateLimitPerHour(String(d.rate_limit_per_hour));
        }
      } catch { /* keep defaults */ }
    })();
  }, []);

  const handleTestEmail = async () => {
    if (!testEmail) return;
    setTestStatus("sending");
    setTestMessage("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/settings/email/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.getAccess()}` },
        body: JSON.stringify({ to_email: testEmail }),
      });
      const data = await res.json();
      setTestStatus(data.success ? "success" : "error");
      setTestMessage(data.message || "");
    } catch {
      setTestStatus("error");
      setTestMessage("Network error — could not reach the server.");
    }
  };

  const handleSave = async () => {
    const body = {
      smtp_host: smtpHost,
      smtp_port: parseInt(smtpPort, 10) || 587,
      smtp_username: smtpUsername,
      smtp_encryption: smtpEncryption,
      from_email: smtpFromEmail,
      from_name: smtpFromName,
      reply_to: smtpReplyTo,
      enabled: smtpEnabled,
      rate_limit_enabled: rateLimitEnabled,
      rate_limit_per_minute: parseInt(rateLimitPerMinute, 10) || 60,
      rate_limit_per_hour: parseInt(rateLimitPerHour, 10) || 1000,
    };
    // Only send password if it was changed
    if (smtpPassword && smtpPassword !== "••••••••") {
      body.smtp_password = smtpPassword;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/settings/email`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.getAccess()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Save failed");
      toast.success("Email configuration saved");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
    setHasChanges(false);
  };

  return (
    <div data-testid="email-configuration-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="email-config-title">
              {t("email.title", "Email Configuration")}
            </h1>
            <HelpIcon featureTag="email_config" portalType="admin" />
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {t("email.subtitle", "Configure SMTP settings and email templates for system notifications")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={smtpEnabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}>
            <Plug className="w-3 h-3 mr-1.5" />
            {smtpEnabled ? t("label.connected", "Connected") : t("label.disconnected", "Disconnected")}
          </Badge>
          {hasChanges && (
            <Button onClick={handleSave} data-testid="save-email-config-btn">
              <Save className="w-4 h-4 mr-2" /> {t("action.save", "Save Changes")}
            </Button>
          )}
        </div>
      </div>

      {/* Connection Status Card */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">12,847</div>
              <div className="text-xs text-slate-500">{t("email.emails_sent_today", "Emails Sent Today")}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">98.7%</div>
              <div className="text-xs text-slate-500">{t("email.delivery_rate", "Delivery Rate")}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">23</div>
              <div className="text-xs text-slate-500">{t("email.bounces_today", "Bounces Today")}</div>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="smtp">{t("email.smtp_settings", "SMTP Settings")}</TabsTrigger>
          <TabsTrigger value="templates">{t("email.templates", "Email Templates")}</TabsTrigger>
          <TabsTrigger value="rate_limits">{t("email.rate_limits", "Rate Limits")}</TabsTrigger>
        </TabsList>

        {/* SMTP Settings Tab */}
        <TabsContent value="smtp" className="m-0">
          <div className="bg-white border border-slate-200 rounded-lg">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">{t("email.smtp_configuration", "SMTP Configuration")}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{t("email.smtp_desc", "Configure outgoing email server settings")}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={smtpEnabled} onCheckedChange={(v) => { setSmtpEnabled(v); setHasChanges(true); }} id="smtp-enabled" />
                    <Label htmlFor="smtp-enabled" className="text-sm">{t("label.enabled", "Enabled")}</Label>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setTestDialogOpen(true)} data-testid="test-email-btn">
                    <TestTube className="w-4 h-4 mr-2" /> {t("email.send_test", "Send Test Email")}
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Preset Selector */}
              <div>
                <Label className="text-xs">{t("email.preset", "Email Provider Preset")}</Label>
                <Select value={smtpPreset} onValueChange={handlePresetChange}>
                  <SelectTrigger className="mt-1 w-full max-w-md" data-testid="smtp-preset-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SMTP_PRESETS.map((preset) => (
                      <SelectItem key={preset.id} value={preset.id}>
                        {preset.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Server Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs">{t("email.smtp_host", "SMTP Host")} *</Label>
                  <div className="relative mt-1">
                    <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={smtpHost}
                      onChange={(e) => { setSmtpHost(e.target.value); setHasChanges(true); }}
                      placeholder="smtp.example.com"
                      className="pl-10"
                      data-testid="smtp-host-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t("email.smtp_port", "SMTP Port")} *</Label>
                  <Select value={smtpPort} onValueChange={(v) => { setSmtpPort(v); setHasChanges(true); }}>
                    <SelectTrigger className="mt-1" data-testid="smtp-port-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25 (SMTP)</SelectItem>
                      <SelectItem value="465">465 (SMTPS/SSL)</SelectItem>
                      <SelectItem value="587">587 (Submission/TLS)</SelectItem>
                      <SelectItem value="2525">2525 (Alternative)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Authentication */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs">{t("email.smtp_username", "Username / API Key")}</Label>
                  <Input
                    value={smtpUsername}
                    onChange={(e) => { setSmtpUsername(e.target.value); setHasChanges(true); }}
                    placeholder="api_user or username"
                    className="mt-1"
                    data-testid="smtp-username-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("email.smtp_password", "Password / API Secret")}</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={smtpPassword}
                      onChange={(e) => { setSmtpPassword(e.target.value); setHasChanges(true); }}
                      placeholder="••••••••"
                      className="pr-10"
                      data-testid="smtp-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Encryption */}
              <div>
                <Label className="text-xs">{t("email.encryption", "Encryption")}</Label>
                <Select value={smtpEncryption} onValueChange={(v) => { setSmtpEncryption(v); setHasChanges(true); }}>
                  <SelectTrigger className="mt-1 w-full max-w-md" data-testid="smtp-encryption-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tls">TLS (Recommended)</SelectItem>
                    <SelectItem value="ssl">SSL</SelectItem>
                    <SelectItem value="none">None (Not Recommended)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-6">
                <h4 className="text-sm font-medium text-slate-900 mb-4">{t("email.sender_settings", "Sender Settings")}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs">{t("email.from_email", "From Email Address")} *</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        value={smtpFromEmail}
                        onChange={(e) => { setSmtpFromEmail(e.target.value); setHasChanges(true); }}
                        placeholder="noreply@qcoresecure.com"
                        className="pl-10"
                        data-testid="smtp-from-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("email.from_name", "From Name")}</Label>
                    <Input
                      value={smtpFromName}
                      onChange={(e) => { setSmtpFromName(e.target.value); setHasChanges(true); }}
                      placeholder="QCoreSecure"
                      className="mt-1"
                      data-testid="smtp-from-name-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">{t("email.reply_to", "Reply-To Address")} ({t("label.optional", "Optional")})</Label>
                    <Input
                      type="email"
                      value={smtpReplyTo}
                      onChange={(e) => { setSmtpReplyTo(e.target.value); setHasChanges(true); }}
                      placeholder="support@qcoresecure.com"
                      className="mt-1 max-w-md"
                      data-testid="smtp-reply-to-input"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">{t("email.reply_to_hint", "If different from the From address")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="templates" className="m-0">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">{t("email.email_templates", "Email Templates")}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{t("email.templates_desc", "Manage notification email templates")}</p>
                </div>
              </div>
            </div>
            <div>
              <div className="grid grid-cols-[1fr_1fr_100px_130px_80px] bg-slate-50 border-b border-slate-200">
                {[t("email.template_name","Template Name"),t("email.subject","Subject"),t("label.status","Status"),t("email.last_modified","Last Modified"),t("label.actions","Actions")].map((h,i) => (
                  <div key={i} className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
                ))}
              </div>
              {EMAIL_TEMPLATES.map((template) => (
                <div key={template.id} className="grid grid-cols-[1fr_1fr_100px_130px_80px] border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`template-row-${template.id}`}>
                  <div className="px-6 py-4 flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-50 rounded flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-900">{template.name}</span>
                  </div>
                  <div className="px-6 py-4 text-sm text-slate-600 flex items-center">{template.subject}</div>
                  <div className="px-6 py-4 flex items-center">
                    <Badge variant="outline" className={template.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}>
                      {template.status === "active" ? t("label.active","Active") : t("label.inactive","Inactive")}
                    </Badge>
                  </div>
                  <div className="px-6 py-4 text-sm text-slate-500 flex items-center">{template.lastModified}</div>
                  <div className="px-6 py-4 flex items-center justify-end">
                    <Button variant="ghost" size="sm">{t("action.edit","Edit")}</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Rate Limits Tab */}
        <TabsContent value="rate_limits" className="m-0">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{t("email.rate_limiting", "Rate Limiting")}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t("email.rate_limit_desc", "Control email sending frequency to prevent abuse")}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={rateLimitEnabled} onCheckedChange={setRateLimitEnabled} id="rate-limit-enabled" />
                <Label htmlFor="rate-limit-enabled" className="text-sm">{t("label.enabled", "Enabled")}</Label>
              </div>
            </div>

            <Alert className="mb-6">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {t("email.rate_limit_info", "Rate limits help protect your email reputation and prevent abuse. These limits apply per tenant.")}
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs">{t("email.per_minute_limit", "Emails Per Minute")}</Label>
                <Input
                  type="number"
                  value={rateLimitPerMinute}
                  onChange={(e) => setRateLimitPerMinute(e.target.value)}
                  placeholder="60"
                  className="mt-1"
                  disabled={!rateLimitEnabled}
                  data-testid="rate-limit-minute-input"
                />
              </div>
              <div>
                <Label className="text-xs">{t("email.per_hour_limit", "Emails Per Hour")}</Label>
                <Input
                  type="number"
                  value={rateLimitPerHour}
                  onChange={(e) => setRateLimitPerHour(e.target.value)}
                  placeholder="1000"
                  className="mt-1"
                  disabled={!rateLimitEnabled}
                  data-testid="rate-limit-hour-input"
                />
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="test-email-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="w-5 h-5" /> {t("email.send_test_email", "Send Test Email")}
            </DialogTitle>
            <DialogDescription id="test-email-desc">
              {t("email.test_email_desc", "Send a test email to verify your SMTP configuration")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {hasChanges && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-sm">
                  You have unsaved changes. The test will use the <strong>last saved</strong> configuration. Save your settings first for accurate results.
                </AlertDescription>
              </Alert>
            )}
            <div>
              <Label className="text-xs">{t("email.recipient_email", "Recipient Email Address")}</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="mt-1"
                data-testid="test-email-input"
              />
            </div>

            {testStatus === "success" && (
              <Alert className="bg-emerald-50 border-emerald-200">
                <Check className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-700 text-sm">
                  {testMessage || t("email.test_success", "Test email sent successfully! Check your inbox.")}
                </AlertDescription>
              </Alert>
            )}

            {testStatus === "error" && (
              <Alert className="bg-red-50 border-red-200">
                <X className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700 text-sm font-mono break-all">
                  {testMessage || "Failed to send test email. Please check your SMTP settings."}
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTestDialogOpen(false); setTestStatus(null); setTestMessage(""); }}>
              {t("action.cancel", "Cancel")}
            </Button>
            <Button onClick={handleTestEmail} disabled={!testEmail || testStatus === "sending"} data-testid="send-test-email-btn">
              {testStatus === "sending" ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> {t("status.sending", "Sending...")}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> {t("email.send_test", "Send Test")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
